import React, { useEffect } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

/**
 * Fixed toast notification for form submit states: loading, success, error.
 * @param {Object} notification - { type: 'loading'|'success'|'error', message: string }
 * @param {Function} onDismiss - optional callback when notification is cleared (e.g. setNotification(null))
 * @param {number} autoDismissMs - auto-dismiss after this many ms (0 = no auto-dismiss). Default 5000 for success/error.
 */
const SubmitNotification = ({ notification, onDismiss, autoDismissMs = 5000 }) => {
  useEffect(() => {
    if (!notification || !onDismiss || notification.type === 'loading') return;
    const t = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(t);
  }, [notification, onDismiss, autoDismissMs]);

  if (!notification || !notification.message) return null;

  const isSuccess = notification.type === 'success';
  const isError = notification.type === 'error';
  const isLoading = notification.type === 'loading';

  const bgClass = isSuccess
    ? 'bg-green-50 border-green-400'
    : isError
    ? 'bg-red-50 border-red-400'
    : isLoading
    ? 'bg-blue-50 border-blue-400'
    : 'bg-gray-50 border-gray-400';

  const textClass = isSuccess
    ? 'text-green-900'
    : isError
    ? 'text-red-900'
    : isLoading
    ? 'text-blue-900'
    : 'text-gray-900';

  return (
    <div
      className={`fixed top-4 right-4 z-[100] p-4 rounded-lg shadow-lg border-2 ${bgClass} min-w-[280px] max-w-md`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {isSuccess && <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />}
        {isError && <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />}
        {isLoading && (
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-medium ${textClass}`}>{notification.message}</p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded hover:bg-black/5 text-gray-500 hover:text-gray-700"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SubmitNotification;
